import { type UseQueryResult, useQueries } from "@tanstack/react-query"
import type { Omit } from "@wagmi/core/internal"
import { hashFn } from "@wagmi/core/query"

import { Evaluate, zeroAddress } from "viem"
import { type Config, useChainId, useConfig } from "wagmi"
import { type GetVaultErrorType, getVault } from "../actions/getVault"
import {
  type GetVaultData,
  type GetVaultOptions,
  type GetVaultQueryFnData,
  type GetVaultQueryKey,
  getVaultQueryKey,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
} from "../types"

export type UseVaultsProps<
  config extends Config = Config,
  selectedData = GetVaultData,
  combineData = UseQueryResult<selectedData>[],
> = ConfigParameter<config> & {
  queries: readonly (GetVaultOptions & {
    query?:
      | Evaluate<
          Omit<
            UseQueryParameters<
              GetVaultQueryFnData,
              GetVaultErrorType,
              GetVaultData,
              GetVaultQueryKey
            >,
            "gcTime" | "staleTime" | "select"
          >
        >
      | undefined
  })[]
  select?: (data: GetVaultData) => selectedData
  combine?: (result: UseQueryResult<selectedData>[]) => combineData
}

export function useVaults<
  config extends ExtendedConfig,
  selectedData = GetVaultData,
  combineData = UseQueryResult<selectedData>[],
>(parameters: UseVaultsProps<config, selectedData, combineData>): combineData {
  const { queries = [], select, combine } = parameters
  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const queryResults = useQueries({
    queries: queries.map(({ scopeKey, query = {}, ...vault }) => {
      const dataQuery = {
        ...query,
        queryKey: getVaultQueryKey({
          address: vault.address || zeroAddress,
          chainId: vault.chainId || chainId,
          evcAddress: vault.evcAddress,
          vaultLensAddress: vault.vaultLensAddress,
          scopeKey,
        }),
        retry: false,
        refetchOnMount: false,
        queryKeyHashFn: hashFn,
        queryFn: () =>
          getVault(config, {
            ...vault,
            chainId: vault.chainId || chainId,
          }),
        select: select,
        enabled:
          !!vault.address &&
          vault.address !== zeroAddress &&
          Boolean(query.enabled ?? true),
      }
      return dataQuery
    }),
  })

  return combine
    ? combine(queryResults as UseQueryResult<selectedData, GetVaultErrorType>[])
    : (queryResults as combineData)
}
